import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { Modal } from '../../custom-essentials';
import { checkResponse } from '../../form';
import { formatDate } from '../../functions';
import PaymentsBodyFooter from './PaymentsBF';

import {
    fetchContractsIds,
    fetchPaymentsIds,
    createPayment,
    updatePayment,
    deletePayment
} from '../../../actions';

function PaymentsModal(props){
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const [submitted, setSubmitted] = useState(false);
    const [payment, setPayment] = useState(null);
    const [contractOption, setContractOption] = useState(null);

    const { 
        show = true,
        mode = 'create',
        payment: paymentProp,
        students = [],
        onClose,
        fetchPaymentsIds,
        fetchContractsIds,
        fetchContractsStudentNames,
        createPayment,
        updatePayment,
        deletePayment
    } = props;

    // Initialize payment and contract data when mode is edit
    useEffect(() => {
        async function init(){
            if(mode !== 'create' && paymentProp?.id){
                const paymentRes = await fetchPaymentsIds({ ids: [paymentProp.id] });
                const contractRes = await fetchContractsIds({ ids: [paymentProp.contract] });
                const newPayment = paymentRes.data?.[0] || {};
                const newContract = contractRes.data?.[0] || {};

                const relStudent = students.find(s => s.user_id === newContract.student) || {};
                const studentName = `${relStudent.first_name || ''} ${relStudent.last_name || ''}`.trim()
                    || `Unknown student (UID: ${newContract.student})`;
                const newContractOption = {
                    value: parseInt(newContract.id),
                    label: `*${formatDate(newContract.start_date)} to ${formatDate(newContract.end_date)}: ${studentName}`
                };

                if(mounted.current){
                    setPayment(newPayment);
                    setContractOption(newContractOption);
                }
            } else if(mode === 'create'){
                // For create mode, payment is null/empty
                if(mounted.current){
                    setPayment(null);
                    setContractOption(null);
                }
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, paymentProp?.id]);

    const handleClose = useCallback((shouldRefresh = false) => {
        if(mounted.current) setSubmitted(false);
        if(onClose) onClose(shouldRefresh);
    }, [onClose]);

    const handleSubmit = useCallback((values, actions) => {
        async function submit(){
            const { setStatus, setSubmitting } = actions;

            if(mounted.current) setSubmitting(true);

            let response = { status: 999 };
            
            if(['create', 'edit'].includes(mode)){
                const paymentObject = {
                    date: values.date,
                    contract: values.contract.value,
                    method: values.method.value,
                    amount: values.amount,
                    notes: values.notes
                };

                if(mode === 'create'){
                    response = await createPayment(paymentObject);
                } else if(mode === 'edit') {
                    paymentObject.id = payment?.id;
                    response = await updatePayment(paymentObject);
                }
            } else if(mode === 'delete') {
                response = await deletePayment({ id: payment?.id });
            }

            const responseValid = checkResponse(response, mounted, setStatus);
            if(!responseValid && mounted.current){
                setSubmitting(false);
                return;
            }
            
            if(mounted.current) setSubmitted(true);
            setTimeout(() => handleClose(true), 1000);
        }
        submit();
    }, [mode, handleClose, payment, createPayment, updatePayment, deletePayment]);

    if(!show) return null;

    return (
        <Modal show={show} onHide={() => handleClose(false)}>
            <Modal.Header>
                <h2>{mode === 'create' ? 'New Payment' : mode === 'edit' ? 'Edit Payment' : 'Delete Payment'}</h2>
            </Modal.Header>
            <Modal.BodyFooter>
                <PaymentsBodyFooter
                    mode={mode}
                    students={students}
                    contractOption={contractOption}
                    selectedPayment={payment}
                    submitted={submitted}
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    fetchContractsStudentNames={fetchContractsStudentNames}
                />
            </Modal.BodyFooter>
        </Modal>
    );
}

export default connect(null, {
    fetchContractsIds,
    fetchPaymentsIds,
    createPayment,
    updatePayment,
    deletePayment
})(PaymentsModal);
